import type { MiddlewareAPI } from 'redux';
import 'rxjs/add/observable/of';
import 'rxjs/add/observable/concat';
import 'rxjs/add/operator/catch';
import 'rxjs/add/operator/mergeMap';
import 'rxjs/add/operator/delay';
import type { ActionsObservable } from 'redux-observable';
import { Observable } from 'rxjs/Observable';
import type { UIAnalyticsEvent } from '@atlaskit/analytics-next';
import fireErrorAnalytics from '@atlassian/jira-errors-handling/src/utils/fire-error-analytics.tsx';
import { fireOperationalAnalytics } from '@atlassian/jira-product-analytics-bridge';
import triggerAnalyticsEvent from '@atlassian/jira-servicedesk-automation-common/src/analytics';
import type { ErrorResponse } from '@atlassian/jira-servicedesk-common/src/utils/fetch';
import type { SummaryRunAsUserSettings } from '../../../model';
import saveRunAsUser from '../../../services/run-as-user/save';
import {
	SAVE_RUN_AS_USER,
	type SaveRunAsUserAction,
	setShowDialog,
	saveRunAsUserSuccess,
	saveRunAsUserFailure,
} from '../../../state/actions/run-as-user';
import { isRunAsUserInvalid } from '../../../state/selectors/run-as-user';
import type { State } from '../../../state/types';

const RUN_AS_USER_SET_ANALYTICS_EVENT = 'run-as-user-set';
const HIDE_DIALOG_DELAY_MS = 500;

const saveSuccessHandler = (
	result: SummaryRunAsUserSettings,
	configWasInvalid: boolean,
	event?: UIAnalyticsEvent, // eslint-disable-next-line @typescript-eslint/no-explicit-any
): Observable<any> => {
	triggerAnalyticsEvent(RUN_AS_USER_SET_ANALYTICS_EVENT);
	event && fireOperationalAnalytics(event, 'serviceDeskAutomationRunAsUser saved');

	// If the run-as-user configuration was invalid, refresh the page after fixing it to remove the configuration error flag
	if (configWasInvalid) {
		window.location.reload();
	}

	return Observable.concat(
		Observable.of(saveRunAsUserSuccess(result)),
		// Delay hiding the dialog to allow the success animation to complete.
		Observable.of(setShowDialog(false)).delay(HIDE_DIALOG_DELAY_MS),
	);
};

// eslint-disable-next-line @typescript-eslint/no-explicit-any
const saveFailureHandler = (error: ErrorResponse): Observable<any> => {
	const { errors } = error.response;
	const errorMessage = errors.map((e) => e.errorMessage).join(', ');
	return Observable.of(saveRunAsUserFailure(errorMessage));
};

// eslint-disable-next-line jira/import/no-anonymous-default-export
export default (action$: ActionsObservable<SaveRunAsUserAction>, store: MiddlewareAPI<State>) =>
	action$.ofType(SAVE_RUN_AS_USER).mergeMap((action) => {
		const state = store.getState();
		const configWasInvalid = isRunAsUserInvalid(state);

		const { accountId, event } = action.payload;
		return saveRunAsUser(state, accountId)
			.mergeMap((result) => saveSuccessHandler(result, configWasInvalid, event))
			.catch((error) => {
				if (error.statusCode !== 400) {
					fireErrorAnalytics({
						meta: {
							id: 'saveRunAsUserFailure',
							packageName: 'jiraServicedeskAutomationSummary',
						},
						error,
						sendToPrivacyUnsafeSplunk: true,
					});
				}
				return saveFailureHandler(error);
			});
	});
